import type {
  InventoryResource,
  ListInventoryItemSnapshotsResponse,
  Snapshot,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { isNativeBackupResource } from '@/app/(dashboard)/inventory/inventoryResourceUtils';
import {
  OptionMenu,
  type OptionType,
} from '@/components/option-menu/option-menu';
import { useViewParameters } from '@/contexts/navigation/useViewParameters';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import { isDateValid } from '@/utils/dateTime';
import { dayjs } from '@/utils/dayjs';

import { DisableAllInclusiveIcon } from './all_inclusive';
import type { MutateSnapshots } from './eonSnapshotsTab';
import { UnprotectSnapshotDialog } from './unprotectSnapshotDialog';

import { Panels } from '../../../panels';

export const SnapshotOptionMenu = ({
  snap,
  mutateSnapshots,
  resource,
}: {
  snap: Snapshot;
  mutateSnapshots: MutateSnapshots;
  resource?: InventoryResource;
}) => {
  const { rightPanel, snackbar } = useWorkspace();
  const { removeParam } = useViewParameters();
  const { isAuthorized } = useRoles();
  const dal = useDAL();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { HoldSnapshotsEnabled } = useFeatureFlags();

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onViewBackupJobClicked = () => {
    rightPanel.setIsOpen(true, PanelSize.Large);
    rightPanel.setComponent({
      panel: Panels.BackupJobsCard,
      props: {
        job: snap.jobId!,
        onClose,
        onBack: () =>
          rightPanel.setComponent({
            panel: Panels.InventoryInstance,
            props: {
              id: snap.resourceId,
              initialTab: 'eon-snapshots',
              onClose,
            },
          }),
      },
    });
  };

  const viewBackupJobOption: OptionType = {
    id: 'view-backup-job',
    text: 'View backup Job',
    icon: 'material-symbols-checklist-rounded',
    isAuthorized: isAuthorized('read:jobs'),
    predicate: () => !!snap.jobId,
    menuItemProps: {
      onClick: onViewBackupJobClicked,
    },
  };

  const updateSnapshotInCache = (newProtectedValue: boolean) => {
    return mutateSnapshots(
      // (key) =>
      //   Array.isArray(key) &&
      //   key[0] === inventoryBaseUrl &&
      //   key[1] === inventorySnapshotsBaseUrl &&
      //   key[2] === snap.resourceId &&
      //   key[5] === 'eon',
      (current: ListInventoryItemSnapshotsResponse | undefined) => {
        if (!current?.data) return current;
        return {
          ...current,
          data: current.data.map((s) =>
            s.id === snap.id ? { ...s, onHold: newProtectedValue } : s
          ),
        };
      },
      { revalidate: false }
    );
  };

  const removeHold = () => {
    return dal.inventory.snapshots
      .removeHold(snap.id)
      .then(() => {
        snackbar.showMessage(
          'Hold removed. The original expiration time now applies.'
        );
        updateSnapshotInCache(false);
      })
      .catch((error) => {
        console.error(error);
        snackbar.showErrorMessage('Failed to remove hold.');
      });
  };

  const hold = () => {
    return dal.inventory.snapshots
      .hold(snap.id)
      .then(() => {
        snackbar.showMessage('Snapshot held.');
        updateSnapshotInCache(true);
      })
      .catch((error) => {
        console.error(error);
        snackbar.showErrorMessage('Failed to hold snapshot.');
      });
  };

  const onHoldSnapshotClicked = () => {
    const retentionExpired =
      isDateValid(snap.expirationTime) &&
      dayjs.utc(snap.expirationTime).isBefore(dayjs.utc());

    if (snap.onHold) {
      if (retentionExpired) {
        setIsDialogOpen(true);
      } else {
        void removeHold();
      }
    } else {
      void hold();
    }
  };

  const isNativeResource = resource ? isNativeBackupResource(resource) : false;

  const holdSnapshotOption: OptionType = {
    id: 'hold-snapshot',
    text: snap.onHold ? 'Remove hold' : 'Hold snapshot',
    icon: snap.onHold ? (
      <DisableAllInclusiveIcon />
    ) : (
      'material-symbols-all-inclusive'
    ),
    // isAuthorized: isAuthorized('update:snapshot_protection'),
    isAuthorized: isAuthorized('create:jobs'),
    predicate: () =>
      HoldSnapshotsEnabled && !snap.supportPiTR && !isNativeResource,
    menuItemProps: {
      onClick: onHoldSnapshotClicked,
    },
  };

  return (
    <>
      <OptionMenu
        placement='top'
        options={[viewBackupJobOption, holdSnapshotOption]}
      />
      <UnprotectSnapshotDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onConfirm={() => {
          void removeHold().then(() => {
            setIsDialogOpen(false);
          });
        }}
      />
    </>
  );
};
